<script setup>
import {
    computed, inject, onMounted, onUpdated, ref, watch,
} from "vue";
import AvailableActivitiesList from "@/components/AvailableActivitiesList.vue";
import EventDetails from "@/components/EventDetails.vue";
import Modal from "@/components/Modal.vue";
import NewSummary from "@/components/NewSummary.vue";
import SelectInput from "@/components/Select/SelectInput.vue";
import RadioInputGroup from "@/components/forms/RadioInputGroup";
import {
    formatRawDate,
    getEventsAmount,
    validateActivityName,
    validateEditDetails,
    validateTimes,
} from "@/components/methods/index.js";
import Steps from "@/components/parts/Steps.vue";
import { useMainStore } from "../../../../stores/mainStore";
import AdvancedDateTime from "./AdvancedDateTime.vue";
import BookingSettings from "../../../../components/BookingSettings.vue";
import { usePricingStore } from "../../../../stores/pricingStore";
import { useBookingSettingsStore } from "../../../../stores/bookingStore";
import TicketsAndPricingV2 from "../../../../components/TicketsAndPriceV2.vue";

const $storage = inject("$storage");
// eslint-disable-next-line no-unused-vars
const props = defineProps({
    showAddEventModal: {
        type: Boolean,
        default: false,
    },
    term: {
        type: Object,
        default() {
            return {
                name: "",
                recurrence: "",
                time: "",
                start: "",
                end: "",
            };
        },
    },
    categories: {
        type: Array,
        default: () => [],
    },
    repetition: {
        type: String,
        default: "weekly",
    },
    triggerReset: {
        type: Boolean,
        default: false,
    },
    availableActivities: {
        type: Array,
        default() {
            return [];
        },
    },
    selectedAvailableActivity: {
        type: Object,
        default() {
            return {
                eventType: "",
                ages: [],
                name: "",
                img: "",
                description: "",
                primaryCategory: [],
                secondaryCategories: [],
                bookingEmail: "",
                bookingType: "",
            };
        },
    },
    activitiesList: {
        type: Array,
        default() {
            return [];
        },
    },
    spinner: {
        type: Object,
        default: () => {
        },
    },
    selectedDay: {
        type: String,
        default: "",
    },
    holidays: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(["close", "submit", "changeAvailableActivity"]);
const mainStore = useMainStore();
const pricingStore = usePricingStore();
const bookingSettingsStore = useBookingSettingsStore();

const eventDetailsComponent = ref(null);

const currentAddEventStep = ref(1);

const listedEvent = ref("true");
const hasPreviousEvents = ref(true);

const selectedActivityIdx = ref(null);

const modal = ref({
    activity: {
        eventType: "",
        ages: [],
        name: "",
        img: "",
        description: "",
        primaryCategory: "",
        secondaryCategories: "",
        bookingEmail: "",
        bookingType: "",
    },
    dates: {
        start: "",
        end: "",
    },
    times: {
        start: "",
        end: "",
    },
    pricing: {
        freeTrial: "",
        priceData: {
            price: "",
        },
        freePricing: false,
    },
    preselectedEvent: {},
});

const modalErrors = ref({
    activity: {},
    times: {},
    pricing: {},
});

const selectedAvailableActivity = ref(null);

const validateCheckActivityNameSubmitted = ref(false);

async function resetModal() {
    // this.showAddCourseModal = false;
    emit("close");
    currentAddEventStep.value = 1;
    modal.value.activity = {
        eventType: "",
        ages: "",
        name: "",
        img: "",
        description: "",
        primaryCategory: "",
        secondaryCategories: "",
        bookingEmail: "",
        bookingType: "",
    };
    modal.value.dates = {
        start: "",
        end: "",
    };
    modal.value.times = {
        start: "",
        end: "",
    };
    modal.value.pricing = {
        freeTrial: "yes",
        priceData: {
            price: "",
        },
        freePricing: false,
    };

    modalErrors.value = {
        activity: {},
        times: {},
        pricing: {},
    };

    await pricingStore.reset($storage);
    await bookingSettingsStore.reset($storage);
}

function closeModal() {
    if (currentAddEventStep.value > 1) {
        currentAddEventStep.value -= 1;
    } else {
        emit("close");
    }
}

const getTotalSteps = computed(() => (pricingStore.sellingTicket ? 5 : 4));

async function submitModal() {
    if (currentAddEventStep.value >= getTotalSteps.value) {
        emit("submit", modal);
    }

    if (currentAddEventStep.value < getTotalSteps.value) {
        let valid = true;
        if (currentAddEventStep.value === 1) {
            if (listedEvent.value === "true") {
                if (selectedActivityIdx.value === null) {
                    valid = false;
                }
            } else {
                const errors = validateEditDetails(modal.value.activity);

                if (Object.values(errors).filter((v) => v !== "").length > 0) {
                    modalErrors.value.activity = errors;
                    valid = false;
                }

                if (validateCheckActivityNameSubmitted.value) {
                    return;
                }

                validateCheckActivityNameSubmitted.value = true;

                const response = await validateActivityName(modal.value.activity.name);

                validateCheckActivityNameSubmitted.value = false;

                // eslint-disable-next-line max-len
                if (
                    modal.value.activity.primaryCategory
                    === modal.value.activity.secondaryCategories
                ) {
                    modal.value.activity.secondaryCategories = null;
                }

                if (modal.value.activity.name && (response.data === "false" && response.errors.name)) {
                    const eventName = modal.value.activity?.eventType?.name?.toLowerCase() ?? "event";
                    const message = response.errors.name.replaceAll(":eventType", eventName);
                    modalErrors.value.activity.name = message;
                    // eslint-disable-next-line consistent-return
                    return false;
                }
            }
        }

        if (currentAddEventStep.value === 2) {
            const timeErrors = validateTimes(modal.value.times);

            if (Object.values(timeErrors).filter((v) => v !== "").length > 0) {
                modalErrors.value.times = timeErrors;
                valid = false;
            }
        }

        if (currentAddEventStep.value === 3) {
            valid = pricingStore.validate();
        }

        if (currentAddEventStep.value === 4) {
            valid = bookingSettingsStore.validate();
            if (valid) {
                bookingSettingsStore.setLocalStorage($storage);
            }
        }

        if (valid) {
            currentAddEventStep.value += 1;
        }
    }
}

function setModalActivity(data) {
    modal.value.activity = data;
}

function updateDateTimes(data) {
    modal.value.times = data.times;
    modal.value.dates = data.dates;
    modal.value.dates.day = data.day;
}

function edit(type) {
    if (type === "details") {
        currentAddEventStep.value = 1;
    }

    if (type === "date-time") {
        currentAddEventStep.value = 2;
    }

    if (type === "price") {
        currentAddEventStep.value = 3;
    }
}

const getCloseText = computed(() => {
    if (currentAddEventStep.value === 1) {
        return "Cancel";
    }
    return "Back";
});

const getSubmitText = computed(() => {
    if (currentAddEventStep.value === getTotalSteps.value) {
        return "Submit and publish";
    }
    return "Next";
});

const getEventType = computed(() => {
    if (!!modal.value.preselectedEvent && !!modal.value.preselectedEvent.eventType) {
        return modal.value.preselectedEvent.eventType.name
            || modal.value.preselectedEvent.eventType;
    }
    if (!!modal.value.activity && !!modal.value.activity.eventType) {
        return modal.value.activity.eventType.name || modal.value.activity.eventType;
    }

    return "";
});

const getBookingSettingsStepNumber = computed(() => (pricingStore.sellingTicket ? 4 : null));

const getAllEventsAmount = computed(() => {
    const repetition = props.repetition === "weekly" ? "week" : "day";
    return getEventsAmount(
        modal.value?.dates?.start,
        modal.value?.dates?.end,
        repetition,
        props.holidays,
    );
});

watch(
    () => props.triggerReset,
    (value) => {
        if (value === true) {
            resetModal();
        }
    },
);

watch(selectedActivityIdx, (value) => {
    modal.value.preselectedEvent = props.activitiesList[value];
});

watch(selectedAvailableActivity, (current) => {
    emit("changeAvailableActivity", current);
});

const eventDetailsActivityData = ref(false);

onUpdated(() => {
    if (
        eventDetailsComponent.value !== null
        && (selectedAvailableActivity.value || listedEvent.value === "true")
    ) {
        eventDetailsActivityData.value = props.selectedAvailableActivity;
    }
});

onMounted(() => {
    if (props.activitiesList.length === 0) {
        listedEvent.value = "false";
        hasPreviousEvents.value = false;
    }

    pricingStore.reset($storage);
});
</script>

<template>
    <Modal
        v-if="showAddEventModal"
        :show-modal="showAddEventModal"
        :new-header="true"
        :header-options="{
            title: term.name,
            recurrence: term.recurrence,
            time: term.time,
        }"
        :default-footer="currentAddEventStep === 1"
        :close-text="getCloseText"
        :submit-text="getSubmitText"
        :close-btn-class="currentAddEventStep === 1 ? '' : 'btn-new btn-new-outline ml-0'"
        :show-spinner="spinner.show || validateCheckActivityNameSubmitted"
        :spinner-color="spinner.color"
        @close-modal="resetModal"
        @submit-delete="closeModal"
        @submit-modal="submitModal"
    >
        <Steps
            :total-steps="getTotalSteps"
            :current-step="currentAddEventStep"
            :show-step-items="false"
            block-class="steps-block"
        />

        <div v-show="currentAddEventStep === 1">
            <h2>Event details</h2>

            <div data-test="radio-group-copy-or-new-event" v-if="hasPreviousEvents">
                <div class="small-title mt-4">What would you like to do?</div>
                <div class="d-flex flex-column pt-0 mt-3">
                    <RadioInputGroup
                        label="title"
                        v-model="listedEvent"
                        :selected="listedEvent"
                        :list="[
                            {
                                title: `I want to add another ${repetition} date and time for
                                    an event I’ve already created`,
                                dataTest: 'radio-copy-exist-event',
                                value: 'true',
                            },
                            {
                                title: 'I want to create a new event',
                                dataTest: 'radio-create-new-event',
                                value: 'false',
                            },
                        ]"
                    />
                </div>
            </div>

            <div v-show="listedEvent === 'true'" class="listed-events-block">
                <div class="small-title mb-1 mt-4">Your events</div>

                <div class="small-subtitle">
                    Select the event you would you like to add a new date and time to.
                </div>

                <div class="listed-event-container">
                    <AvailableActivitiesList
                        :list="props.activitiesList"
                        :selected-idx="selectedActivityIdx"
                        @idx-change="selectedActivityIdx = $event"
                    />
                </div>
            </div>
            <div v-show="listedEvent === 'false'">
                <div class="d-flex flex-column select-container mt-5 mb-3">
                    <label class="show">Copy event details (optional)</label>
                    <div class="small-subtitle">
                        To save time, you can copy details from an event you’ve already created.
                    </div>
                    <SelectInput
                        class="available-activities"
                        :options="$props.availableActivities"
                        label="title"
                        placeholder="Choose an event"
                        :multiple="false"
                        :default="selectedAvailableActivity"
                        @change="selectedAvailableActivity = $event"
                    />
                </div>

                <EventDetails
                    ref="eventDetailsComponent"
                    :default="{
                        bookingEmail: mainStore.defaultEmailForBusiness,
                    }"
                    :default-categories="categories"
                    :errors="modalErrors.activity"
                    :set-activity-data="eventDetailsActivityData"
                    @updated="setModalActivity"
                    @set-img="modal.activity.img = $event"
                />
            </div>
        </div>

        <div v-if="currentAddEventStep === 2">
            <AdvancedDateTime
                :event-type="getEventType"
                :start="new Date(term.start)"
                :end="new Date(term.end)"
                :errors="modalErrors.times"
                :recurrence="repetition"
                :day="selectedDay"
                @updated="updateDateTimes"
            />
        </div>

        <div v-if="currentAddEventStep === 3">
            <TicketsAndPricingV2
                :event-type="getEventType"
                :recurring-type="props.repetition"
                :all-events-amount="getAllEventsAmount"
            />
        </div>

        <div v-if="currentAddEventStep === getBookingSettingsStepNumber">
            <BookingSettings :storage="$storage"/>
        </div>

        <div v-if="currentAddEventStep === getTotalSteps">
            <h2>Summary</h2>
            <NewSummary
                :activity="listedEvent === 'true' ? modal.preselectedEvent : modal.activity || {}"
                :img="listedEvent === 'true' ? modal.preselectedEvent.img : modal.activity?.img"
                :dates="{
                    start: formatRawDate(modal.dates.start) ?? '',
                    end: formatRawDate(modal.dates.end) ?? '',
                    startRaw: modal.dates.start,
                    endRaw: modal.dates.end,
                    repeat: props.repetition === 'weekly' ? 'week' : 'day',
                }"
                :times="modal.times"
                :holidays="[]"
                :add-holiday="'no'"
                :price="{
                    price: modal.pricing.priceData.price,
                    pricePerTerm: modal.pricing.priceData.pricePerTerm,
                    freePricing: modal.pricing.freePricing,
                    freeTrial: modal.pricing.freeTrial === 'yes' ? 'Yes' : 'No',
                }"
                :ages="
                    listedEvent === 'true'
                        ? modal.preselectedEvent.ages
                        : Object.values(modal.activity.ages)
                            ?.map((el) => el.name)
                            .join(', ') || ''
                "
                :main-category="
                    listedEvent === 'true'
                        ? modal.preselectedEvent.primaryCategory
                        : modal.activity.primaryCategory.name
                "
                :secondary-category="
                    listedEvent === 'true'
                        ? modal.preselectedEvent.secondaryCategories?.name
                        : modal.activity.secondaryCategories?.name
                "
                @edit-click="edit($event)"
                :priceV2="pricingStore.getPricingState"
            />
        </div>
    </Modal>
</template>

<style scoped>
h2 {
    font-style: normal;
    font-weight: 600;
    font-size: 1.5rem;
    line-height: 2.25rem;
    margin-bottom: 2.3rem;
}

.listed-event-container {
    height: 186px;
    padding-right: 8px;
    padding-left: 2px;
    overflow-y: scroll;
}

.steps-block {
    margin-top: 3rem;
    margin-bottom: 0.5rem;
}
</style>
